import { useTranslation } from 'react-i18next';
import type { VehicleType } from '~/graphql/graphql.ts';
import { getActiveFacility } from '~/hooks/useTransportState';
import { buildVehicleGroups, type VehicleOption } from '~/utils/vehicleGroups';
import type { DomesticDeliveryMethod } from '~/types/general';
import { WarningBanner } from '~/components/ui/Banner';

export type { DomesticDeliveryMethod };

type FacilityLike = {
  id: string;
};

interface TransportCardProps {
  sellerOrganizationFacility: FacilityLike;
  shippingLocationFacility?: FacilityLike;
  deliveryMethod: DomesticDeliveryMethod;
  selectedTransportVehicleId: string;
  supportedVehicleIds?: string[];
  disabled?: boolean;
  isInternational?: boolean;
  transportVehicleCatalog?: VehicleOption[];
  onTransportChange: (
    deliveryMethod: DomesticDeliveryMethod,
    transportVehicleId: string,
    vehicleType?: VehicleType
  ) => void;
}

export function TransportCard({
  sellerOrganizationFacility,
  shippingLocationFacility,
  deliveryMethod,
  selectedTransportVehicleId,
  supportedVehicleIds,
  transportVehicleCatalog,
  onTransportChange,
  disabled,
  isInternational,
}: Readonly<TransportCardProps>) {
  const { t } = useTranslation(['cart', 'settings']);

  const activeFacility = getActiveFacility(
    deliveryMethod,
    shippingLocationFacility,
    sellerOrganizationFacility
  );
  const activeVehicles = activeFacility ? (transportVehicleCatalog ?? []) : [];

  const sortedVehicleGroups = buildVehicleGroups(
    activeVehicles,
    supportedVehicleIds
  );

  const effectiveVehicleGroups = isInternational
    ? sortedVehicleGroups.filter(([type]) => type === 'INTERNATIONAL_CONTAINER')
    : sortedVehicleGroups.filter(
        ([type]) => type !== 'INTERNATIONAL_CONTAINER'
      );

  const handleDeliveryMethodChange = (method: DomesticDeliveryMethod) => {
    const targetFacility = getActiveFacility(
      method,
      shippingLocationFacility,
      sellerOrganizationFacility
    );
    const targetVehicles = targetFacility
      ? (transportVehicleCatalog ?? [])
      : [];
    const currentVehicle = targetVehicles.find(
      (v) => v.id === selectedTransportVehicleId
    );
    const targetVehicleGroups = buildVehicleGroups(
      targetVehicles,
      supportedVehicleIds
    );
    const fallbackVehicle = targetVehicleGroups[0]?.[1]?.[0];
    const vehicle = currentVehicle ?? fallbackVehicle;
    onTransportChange(method, vehicle?.id || '', vehicle?.vehicleType);
  };

  const handleVehicleChange = (vehicleId: string) => {
    const vehicle = activeVehicles.find((v) => v.id === vehicleId);
    onTransportChange(deliveryMethod, vehicleId, vehicle?.vehicleType);
  };

  return (
    <div className="bg-neut-0 p-3 flex flex-col gap-3">
      <h4 className="text-sm text-neut-prim font-normal">
        {isInternational ? t('cart.container') : t('cart.delivery_method')}
      </h4>
      {!isInternational && (
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="delivery-method-pickup"
            onClick={() => handleDeliveryMethodChange('pickup')}
            disabled={disabled}
            className={`flex-1 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
              deliveryMethod === 'pickup'
                ? 'bg-prim-500 text-neut-0 border-prim-500 hover:bg-prim-hover'
                : 'bg-neut-0 text-neut-600 border-neut-300 hover:border-prim-500 hover:text-prim-500'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {t('cart.delivery_method_pickup')}
          </button>
          <button
            type="button"
            data-testid="delivery-method-delivered"
            onClick={() => handleDeliveryMethodChange('delivered')}
            disabled={disabled}
            className={`flex-1 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
              deliveryMethod === 'delivered'
                ? 'bg-prim-500 text-neut-0 border-prim-500 hover:bg-prim-hover'
                : 'bg-neut-0 text-neut-600 border-neut-300 hover:border-prim-500 hover:text-prim-500'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {t('cart.delivery_method_delivered')}
          </button>
        </div>
      )}
      <div className="">
        {isInternational &&
          activeFacility &&
          effectiveVehicleGroups.length === 0 && (
            <WarningBanner className="mb-2">
              {t('cart.international.no_container_vehicles')}
            </WarningBanner>
          )}
        <select
          data-testid="transport-vehicle-select"
          value={selectedTransportVehicleId}
          onChange={(e) => handleVehicleChange(e.target.value)}
          disabled={disabled}
          className="w-full text-xs border border-neut-300 rounded-lg px-2 py-1.5 focus:outline-none focus:border-prim-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">{t('cart.select_transport_mode')}</option>
          {!activeFacility ? (
            <option value="" disabled>
              {t('cart.select_address_first')}
            </option>
          ) : (
            effectiveVehicleGroups.map(([type, vehicles]) => (
              <optgroup
                key={type}
                label={t(
                  `settings:settings.facilities.transport_modes.vehicle_types.${type}` as const
                )}
              >
                {vehicles.map((v: VehicleOption) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </optgroup>
            ))
          )}
        </select>
      </div>
    </div>
  );
}
